import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RegisterPaymentModal from './RegisterPaymentModal';
import './ClientInfoPanel.css';

const ClientInfoPanel = ({ contact }) => {
  const [clientData, setClientData] = useState(null);
  const [services, setServices] = useState([]); // multi-servicio: [{id, client, unpaidInvoices, allInvoices}]
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Estados para aplicar el recibo de este chat a otro cliente
  const [applyOtherId, setApplyOtherId] = useState('');
  const [applyOtherLoading, setApplyOtherLoading] = useState(false);
  const [applyOtherMsg, setApplyOtherMsg] = useState(null);

  const applyReceiptToOther = async () => {
    const target = applyOtherId.trim();
    if (!target) {
      setApplyOtherMsg({ ok: false, text: 'Escribe el ID del cliente destino' });
      return;
    }
    setApplyOtherLoading(true);
    setApplyOtherMsg(null);
    try {
      const response = await axios.post('/api/receipts/apply-to-client', {
        phone: contact.phoneNumber,
        targetClientId: target,
      });
      setApplyOtherMsg({ ok: true, text: response.data.message || ('Pago aplicado al cliente ' + target) });
      setApplyOtherId('');
    } catch (e) {
      const txt = (e.response && e.response.data && e.response.data.error) || 'Error al aplicar el pago';
      setApplyOtherMsg({ ok: false, text: txt });
    } finally {
      setApplyOtherLoading(false);
    }
  };

  // Estados para modal "Agregar Número"
  const [showLinkPhoneModal, setShowLinkPhoneModal] = useState(false);
  const [linkPhoneId, setLinkPhoneId] = useState('');
  const [linkPhoneLoading, setLinkPhoneLoading] = useState(false);
  const [linkPhoneResult, setLinkPhoneResult] = useState(null);
  const [linkPhoneError, setLinkPhoneError] = useState('');

  // Estados para WiFi
  const [wifiData, setWifiData] = useState(null);
  const [loadingWifi, setLoadingWifi] = useState(false);
  const [editingWifi, setEditingWifi] = useState(false);
  const [editingSSID, setEditingSSID] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [newSSID, setNewSSID] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (contact) {
      loadClientData();
      loadWifiData();
    }
  }, [contact]);

  const getAllMikrowispIds = (contact) => {
    const seen = new Set();
    const add = (raw) => {
      const n = parseInt(String(raw || '').replace(/\D/g, ''), 10);
      if (n > 0) seen.add(String(n));
    };
    if (contact?.mikrowispId) add(contact.mikrowispId);
    if (Array.isArray(contact?.mikrowispIds)) {
      contact.mikrowispIds.forEach(add);
    }
    if (contact?.notes) {
      const re = /(?:mikrowisp\s*id|id\s*mikrowisp)\s*[:#]?\s*(\d{1,10})/gi;
      let m;
      while ((m = re.exec(contact.notes)) !== null) add(m[1]);
    }
    return Array.from(seen).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  };

  const getMikrowispId = (contact) => {
    const ids = getAllMikrowispIds(contact);
    return ids.length ? ids[0] : null;
  };

  // ── Vincular número a ID de cliente Mikrowisp ─────────────────────────────
  const handleLinkPhone = async () => {
    if (!linkPhoneId.trim()) return;
    setLinkPhoneLoading(true);
    setLinkPhoneResult(null);
    setLinkPhoneError('');
    try {
      const resp = await axios.post('/api/contacts/link-phone', {
        phoneNumber: contact.phoneNumber,
        mikrowispId: linkPhoneId.trim()
      });
      setLinkPhoneResult(resp.data);
      setLinkPhoneId('');
      // Recargar datos del cliente tras vincular
      setTimeout(() => { loadClientData(); }, 800);
    } catch (e) {
      setLinkPhoneError(e.response?.data?.error || e.message || 'Error al vincular');
    } finally {
      setLinkPhoneLoading(false);
    }
  };

  const loadClientData = async () => {
    const mikrowispIds = getAllMikrowispIds(contact);
    if (!mikrowispIds.length) {
      setClientData(null);
      setServices([]);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const loaded = [];
      for (const mikrowispId of mikrowispIds) {
        try {
          const response = await axios.post('/api/contacts/get-mikrowisp-client', {
            id: parseInt(mikrowispId, 10)
          });
          if (!(response.data.success && response.data.client)) continue;
          let allInvoices = [];
          try {
            const invRes = await axios.post('/api/contacts/get-client-invoices-all', {
              idcliente: parseInt(mikrowispId, 10)
            });
            if (invRes.data?.success) allInvoices = invRes.data.invoices || [];
          } catch (_) { /* ignore */ }
          const unpaidInvoices = allInvoices.filter((inv) => {
            const estado = String(inv?.estado || '').toLowerCase();
            const total = parseFloat(inv?.total || 0);
            const cobrado = parseFloat(inv?.cobrado || 0);
            return estado.includes('no pagado') || estado === 'pendiente' || (total - cobrado > 0.01 && !estado.includes('pagad'));
          });
          loaded.push({
            id: mikrowispId,
            client: response.data.client,
            unpaidInvoices,
            allInvoices
          });
        } catch (e) {
          console.error('Error cargando ID', mikrowispId, e);
        }
      }

      if (!loaded.length) {
        setClientData(null);
        setServices([]);
        setError(`No se encontraron los IDs ${mikrowispIds.join(', ')} en Mikrowisp.`);
        return;
      }

      setServices(loaded);
      setClientData(loaded[0].client);
      setInvoices(loaded.flatMap((s) => (s.allInvoices || []).map((inv) => ({ ...inv, _clientId: s.id }))));
      setError('');
    } catch (error) {
      console.error('Error cargando datos del cliente:', error);
      const errorMsg = error.response?.data?.error || 'Error al cargar informacion del cliente';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loadWifiData = async () => {
    if (!contact || !contact.phoneNumber) return;
    
    try {
      setLoadingWifi(true);
      const response = await axios.get(`/api/wifi/admin/client/${contact.phoneNumber}`);
      
      if (response.data.success) {
        setWifiData(response.data.wifiData);
        setNewSSID(response.data.wifiData.ssid);
      }
    } catch (error) {
      console.error('Error cargando datos WiFi:', error);
      // No mostrar error si simplemente no hay datos WiFi
      setWifiData(null);
    } finally {
      setLoadingWifi(false);
    }
  };

  const handleUpdateSSID = async () => {
    if (!newSSID || newSSID.trim() === '') {
      alert('❌ El SSID no puede estar vacío');
      return;
    }

    if (newSSID.length > 32) {
      alert('❌ El SSID no puede exceder 32 caracteres');
      return;
    }

    try {
      setLoadingWifi(true);
      const response = await axios.put(`/api/wifi/admin/client/${contact.phoneNumber}/ssid`, {
        ssid: newSSID.trim()
      });

      if (response.data.success) {
        alert('✅ SSID actualizado correctamente');
        setEditingSSID(false);
        loadWifiData(); // Recargar datos
      }
    } catch (error) {
      console.error('Error actualizando SSID:', error);
      alert('❌ Error: ' + (error.response?.data?.error || 'No se pudo actualizar el SSID'));
    } finally {
      setLoadingWifi(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.trim() === '') {
      alert('❌ La contraseña no puede estar vacía');
      return;
    }

    if (newPassword.length < 8) {
      alert('❌ La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (newPassword.length > 63) {
      alert('❌ La contraseña no puede exceder 63 caracteres');
      return;
    }

    try {
      setLoadingWifi(true);
      const response = await axios.put(`/api/wifi/admin/client/${contact.phoneNumber}/password`, {
        password: newPassword
      });

      if (response.data.success) {
        alert('✅ Contraseña actualizada correctamente');
        setEditingPassword(false);
        setNewPassword('');
        setShowPassword(false);
        loadWifiData(); // Recargar datos
      }
    } catch (error) {
      console.error('Error actualizando contraseña:', error);
      alert('❌ Error: ' + (error.response?.data?.error || 'No se pudo actualizar la contraseña'));
    } finally {
      setLoadingWifi(false);
    }
  };

  const handleUpdateWifi = async () => {
    // Validar SSID
    if (!newSSID || newSSID.trim() === '') {
      alert('❌ El SSID no puede estar vacío');
      return;
    }

    if (newSSID.length > 32) {
      alert('❌ El SSID no puede exceder 32 caracteres');
      return;
    }

    // Validar contraseña
    if (!newPassword || newPassword.trim() === '') {
      alert('❌ La contraseña no puede estar vacía');
      return;
    }

    if (newPassword.length < 8) {
      alert('❌ La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (newPassword.length > 63) {
      alert('❌ La contraseña no puede exceder 63 caracteres');
      return;
    }

    if (!window.confirm(`¿Confirma cambiar el WiFi?\n\nNuevo SSID: ${newSSID.trim()}\nNueva contraseña: ${newPassword}`)) {
      return;
    }

    try {
      setLoadingWifi(true);

      // Cambiar SSID
      const ssidResponse = await axios.put(`/api/wifi/admin/client/${contact.phoneNumber}/ssid`, {
        ssid: newSSID.trim()
      });

      if (!ssidResponse.data.success) {
        throw new Error('Error al actualizar SSID');
      }

      // Cambiar contraseña
      const passResponse = await axios.put(`/api/wifi/admin/client/${contact.phoneNumber}/password`, {
        password: newPassword
      });

      if (!passResponse.data.success) {
        throw new Error('Error al actualizar contraseña');
      }

      alert('✅ WiFi actualizado correctamente\n\nSSID: ' + newSSID.trim() + '\nContraseña: ' + newPassword);
      setEditingWifi(false);
      setShowPassword(false);
      loadWifiData(); // Recargar datos

    } catch (error) {
      console.error('Error actualizando WiFi:', error);
      alert('❌ Error: ' + (error.response?.data?.error || error.message || 'No se pudo actualizar el WiFi'));
    } finally {
      setLoadingWifi(false);
    }
  };

  const formatMoney = (v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return '0.00';
    return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const totalDebt = services.reduce((sum, s) => {
    const d = parseFloat(s?.client?.facturacion?.total_facturas || 0);
    return sum + (Number.isNaN(d) ? 0 : d);
  }, 0);

  const totalUnpaidCount = services.reduce((sum, s) => sum + (s.unpaidInvoices?.length || 0), 0);

  const mikrowispId = getMikrowispId(contact);

  if (!mikrowispId) {
    return (
      <div className="client-info-panel">
        <div className="client-info-header">
          <h3>👤 Información del Cliente</h3>
          <div className="header-buttons">
            <button
              onClick={() => { setShowLinkPhoneModal(true); setLinkPhoneResult(null); setLinkPhoneError(''); setLinkPhoneId(''); }}
              className="link-phone-button"
              title="Agregar número a cliente Mikrowisp"
            >
              📱 Agregar Número
            </button>
          </div>
        </div>
        <div className="client-info-empty">
          <p>Este contacto no tiene ID de Mikrowisp</p>
          <p className="empty-hint">Sincroniza desde Mikrowisp para ver la información</p>
        </div>
        {/* Modal: Agregar Número a Mikrowisp */}
        {showLinkPhoneModal && (
          <div className="link-phone-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowLinkPhoneModal(false); }}>
            <div className="link-phone-modal">
              <div className="link-phone-modal-header">
                <h3>📱 Agregar Número al Cliente</h3>
                <button className="link-phone-close-btn" onClick={() => setShowLinkPhoneModal(false)}>✕</button>
              </div>
              <div className="link-phone-modal-body">
                <p>Vincula el número <strong>{contact?.phoneNumber}</strong> a un cliente Mikrowisp ingresando su ID:</p>
                <div className="link-phone-input-row">
                  <input
                    type="text"
                    className="link-phone-input"
                    placeholder="Ej: 513"
                    value={linkPhoneId}
                    onChange={(e) => { setLinkPhoneId(e.target.value.replace(/\D/g, '')); setLinkPhoneError(''); setLinkPhoneResult(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleLinkPhone()}
                    disabled={linkPhoneLoading}
                  />
                  <button
                    className="link-phone-confirm-btn"
                    onClick={handleLinkPhone}
                    disabled={!linkPhoneId.trim() || linkPhoneLoading}
                  >
                    {linkPhoneLoading ? '⏳' : '✔ Vincular'}
                  </button>
                </div>
                {linkPhoneError && <p className="link-phone-error">❌ {linkPhoneError}</p>}
                {linkPhoneResult && (
                  <p className="link-phone-success">
                    ✅ Número vinculado a <strong>{linkPhoneResult.clienteName}</strong> (ID: {linkPhoneResult.mikrowispId})
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="client-info-panel">
      <div className="apply-other-box" style={{ background: '#f0f7ff', border: '1px solid #b9d7f2', borderRadius: 8, padding: '8px 10px', margin: '6px 10px' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>💵 Aplicar pago de este chat a otro cliente</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={applyOtherId}
            onChange={(e) => setApplyOtherId(e.target.value)}
            placeholder="ID del cliente (ej. 755)"
            style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid #c3c9d1' }}
            onKeyDown={(e) => { if (e.key === 'Enter') applyReceiptToOther(); }}
          />
          <button
            onClick={applyReceiptToOther}
            disabled={applyOtherLoading}
            style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}
          >
            {applyOtherLoading ? 'Aplicando…' : 'Aplicar'}
          </button>
        </div>
        {applyOtherMsg && (
          <div style={{ marginTop: 4, fontSize: 13, color: applyOtherMsg.ok ? '#1b7e3c' : '#c62828' }}>
            {applyOtherMsg.ok ? '✅ ' : '⚠️ '}{applyOtherMsg.text}
          </div>
        )}
      </div>
      <div className="client-info-header">
        <h3>👤 Información del Cliente</h3>
        <div className="header-buttons">
          {(!contact?.mikrowispId || String(contact.mikrowispId).trim() === '' || String(contact.mikrowispId).trim() === '0') && (
            <button
              onClick={() => { setShowLinkPhoneModal(true); setLinkPhoneResult(null); setLinkPhoneError(''); setLinkPhoneId(''); }}
              className="link-phone-button"
              title="Agregar número a cliente Mikrowisp"
            >
              📱 Agregar Número
            </button>
          )}
          <button
            onClick={() => setShowPaymentModal(true)}
            className="register-payment-button"
            title="Registrar pago"
          >
            💳 Registrar Pago
          </button>
          <button
            onClick={loadClientData}
            className="refresh-button"
            disabled={loading}
            title="Actualizar información"
          >
            {loading ? '🔄' : '🔄'}
          </button>
        </div>
      </div>

      {/* Modal: Agregar Número a Mikrowisp */}
      {showLinkPhoneModal && (
        <div className="link-phone-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowLinkPhoneModal(false); }}>
          <div className="link-phone-modal">
            <div className="link-phone-modal-header">
              <h3>📱 Agregar Número al Cliente</h3>
              <button className="link-phone-close-btn" onClick={() => setShowLinkPhoneModal(false)}>✕</button>
            </div>
            <div className="link-phone-modal-body">
              <p>Vincula el número <strong>{contact?.phoneNumber}</strong> a un cliente Mikrowisp ingresando su ID:</p>
              <div className="link-phone-input-row">
                <input
                  type="text"
                  className="link-phone-input"
                  placeholder="Ej: 513"
                  value={linkPhoneId}
                  onChange={(e) => { setLinkPhoneId(e.target.value.replace(/\D/g, '')); setLinkPhoneError(''); setLinkPhoneResult(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleLinkPhone()}
                  disabled={linkPhoneLoading}
                />
                <button
                  className="link-phone-confirm-btn"
                  onClick={handleLinkPhone}
                  disabled={!linkPhoneId.trim() || linkPhoneLoading}
                >
                  {linkPhoneLoading ? '⏳' : '✔ Vincular'}
                </button>
              </div>
              {linkPhoneError && <p className="link-phone-error">❌ {linkPhoneError}</p>}
              {linkPhoneResult && (
                <p className="link-phone-success">
                  ✅ Número vinculado a <strong>{linkPhoneResult.clienteName}</strong> (ID: {linkPhoneResult.mikrowispId})
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && !clientData && (
        <div className="client-info-loading">
          <p>Cargando información...</p>
        </div>
      )}

      {error && (
        <div className="client-info-error">
          <p>❌ {error}</p>
          <button onClick={loadClientData} className="retry-button">
            Reintentar
          </button>
        </div>
      )}

      {clientData && (
        <div className="client-info-content">
          {services.length > 1 && (
            <div className="multi-service-banner">
              Cliente con {services.length} servicios
            </div>
          )}

          <div className="info-section">
            <div className="info-label">ID Cliente:</div>
            <div className="info-value">
              {services.length > 1
                ? services.map((s) => s.id).join(' / ')
                : (clientData.id || mikrowispId)}
            </div>
          </div>

          <div className="info-section">
            <div className="info-label">Nombre:</div>
            <div className="info-value">{clientData.nombre || 'N/A'}</div>
          </div>

          <div className="info-section">
            <div className="info-label">Estado:</div>
            <div className={`info-value status-${clientData.estado?.toLowerCase() || 'desconocido'}`}>
              {clientData.estado || 'N/A'}
            </div>
          </div>

          <div className="info-section">
            <div className="info-label">Deuda Actual:</div>
            <div className="info-value debt">
              ${services.length > 1 ? formatMoney(totalDebt) : (clientData.facturacion?.total_facturas || '0.00')}
            </div>
          </div>

          <div className="info-section">
            <div className="info-label">Facturas No Pagadas:</div>
            <div className="info-value">
              {services.length > 1 ? totalUnpaidCount : (clientData.facturacion?.facturas_nopagadas || 0)}
            </div>
          </div>

          {services.length > 1 && (
            <div className="info-section multi-service-block">
              <div className="info-label">Información de pago (por servicio):</div>
              <div className="multi-service-list">
                {services.map((svc) => {
                  const debt = parseFloat(svc.client?.facturacion?.total_facturas || 0) || 0;
                  const unpaid = svc.unpaidInvoices || [];
                  return (
                    <div key={svc.id} className="multi-service-card">
                      <div className="multi-service-title">ID {svc.id} · {svc.client?.estado || 'N/A'}</div>
                      <div className="multi-service-row">Deuda: <strong>${formatMoney(debt)}</strong></div>
                      <div className="multi-service-row">Dirección: {svc.client?.direccion_principal || 'N/A'}</div>
                      {unpaid.length === 0 ? (
                        <div className="multi-service-row unpaid-ok">Sin facturas pendientes</div>
                      ) : (
                        unpaid.map((inv, idx) => {
                          const pend = (parseFloat(inv.total) || 0) - (parseFloat(inv.cobrado) || 0);
                          const show = pend > 0.01 ? pend : (parseFloat(inv.total) || 0);
                          return (
                            <div key={inv.id || idx} className="multi-service-invoice">
                              Factura {inv.id || inv.factura || '—'}: RD$ {formatMoney(show)}
                              {inv.vencimiento && inv.vencimiento !== '0000-00-00' ? ` · vence ${inv.vencimiento}` : ''}
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="info-section">
            <div className="info-label">Dirección:</div>
            <div className="info-value">
              {services.length > 1
                ? services.map((s) => `ID ${s.id}: ${s.client?.direccion_principal || 'N/A'}`).join(' | ')
                : (clientData.direccion_principal || 'No disponible')}
            </div>
          </div>

          <div className="info-section">
            <div className="info-label">Fecha Límite de Pago:</div>
            <div className="info-value">
              {(() => {
                // Log siempre visible
                console.log('RENDER - Fecha Límite de Pago - invoices:', invoices);
                console.log('RENDER - Fecha Límite de Pago - invoices.length:', invoices?.length);
                
                // Simplificar: buscar cualquier factura con vencimiento
                if (!invoices || invoices.length === 0) {
                  console.log('RENDER - No hay facturas en el array');
                  return 'No disponible (sin facturas)';
                }
                
                console.log('RENDER - Hay facturas, buscando no pagadas...');
                
                // Buscar factura no pagada con vencimiento
                const facturaNoPagada = invoices.find(inv => {
                  if (!inv) return false;
                  const estado = (inv.estado || '').toLowerCase();
                  const tieneVencimiento = inv.vencimiento && inv.vencimiento !== '0000-00-00' && inv.vencimiento !== '';
                  const esNoPagada = estado.includes('no pagado') || estado === 'pendiente' || (estado !== 'pagada' && estado !== 'pagado');
                  return esNoPagada && tieneVencimiento;
                });
                
                if (facturaNoPagada && facturaNoPagada.vencimiento) {
                  try {
                    const [year, month, day] = facturaNoPagada.vencimiento.split('-').map(Number);
                    const fecha = new Date(year, month - 1, day);
                    if (!isNaN(fecha.getTime())) {
                      return fecha.toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    }
                    return facturaNoPagada.vencimiento;
                  } catch (e) {
                    return facturaNoPagada.vencimiento;
                  }
                }
                
                return 'No disponible (sin facturas pendientes)';
              })()}
            </div>
          </div>

          <div className="info-section">
            <div className="info-label">Último Día de Pago:</div>
            <div className="info-value">
              {(() => {
                // Log siempre visible
                console.log('RENDER - Último Día de Pago - invoices:', invoices);
                console.log('RENDER - Último Día de Pago - invoices.length:', invoices?.length);
                
                // Simplificar: buscar cualquier factura pagada con fecha de pago
                if (!invoices || invoices.length === 0) {
                  console.log('⚠️ RENDER - No hay facturas para último día de pago');
                  return 'No disponible (sin facturas)';
                }
                
                console.log('✅ RENDER - Hay facturas, buscando pagadas...');
                
                // Buscar facturas pagadas con fecha de pago
                const facturasPagadas = invoices.filter(inv => {
                  if (!inv) return false;
                  const estado = (inv.estado || '').toLowerCase();
                  const tieneFechaPago = inv.fechapago && inv.fechapago !== '0000-00-00' && inv.fechapago !== '';
                  const esPagada = estado === 'pagada' || estado === 'pagado';
                  return esPagada && tieneFechaPago;
                });
                
                if (facturasPagadas.length > 0) {
                  // Ordenar por fecha de pago descendente
                  facturasPagadas.sort((a, b) => {
                    try {
                      return new Date(b.fechapago) - new Date(a.fechapago);
                    } catch (e) {
                      return 0;
                    }
                  });
                  
                  const masReciente = facturasPagadas[0];
                  if (masReciente.fechapago) {
                    try {
                      const [year, month, day] = masReciente.fechapago.split('-').map(Number);
                      const fecha = new Date(year, month - 1, day);
                      if (!isNaN(fecha.getTime())) {
                        return fecha.toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      }
                      return masReciente.fechapago;
                    } catch (e) {
                      return masReciente.fechapago;
                    }
                  }
                }
                
                return 'No disponible (sin pagos registrados)';
              })()}
            </div>
          </div>

          {clientData.servicios && clientData.servicios.length > 0 && clientData.servicios[0].instalado && clientData.servicios[0].instalado !== '0000-00-00' && (
            <div className="info-section">
              <div className="info-label">Fecha de Instalación:</div>
              <div className="info-value">
                {new Date(clientData.servicios[0].instalado).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          )}

          {clientData.servicios && clientData.servicios.length > 0 && (
            <div className="info-section services-section">
              <div className="info-label">Servicios PPPOE:</div>
              <div className="services-list">
                {clientData.servicios.map((servicio, index) => (
                  <div key={index} className="service-item">
                    <div className="service-ppp">
                      <span className="service-label">Usuario:</span>
                      <span className="service-value">{servicio.pppuser || 'N/A'}</span>
                    </div>
                    <div className="service-ppp">
                      <span className="service-label">Contraseña:</span>
                      <span className="service-value">{servicio.ppppass || 'N/A'}</span>
                    </div>
                    <div className="service-details">
                      <span className="service-label">IP:</span>
                      <span className="service-value">{servicio.ip || 'N/A'}</span>
                    </div>
                    <div className="service-details">
                      <span className="service-label">Estado:</span>
                      <span className={`service-status status-${servicio.status_user?.toLowerCase() || 'offline'}`}>
                        {servicio.status_user || 'N/A'}
                      </span>
                    </div>
                    <div className="service-details">
                      <span className="service-label">Perfil:</span>
                      <span className="service-value">{servicio.perfil || 'N/A'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {clientData.telefono && (
            <div className="info-section">
              <div className="info-label">Teléfono:</div>
              <div className="info-value">{clientData.telefono}</div>
            </div>
          )}

          {clientData.movil && (
            <div className="info-section">
              <div className="info-label">Móvil:</div>
              <div className="info-value">{clientData.movil}</div>
            </div>
          )}

          {/* Sección de Gestión WiFi */}
          <div className="wifi-management-section">
            <div className="wifi-section-header">
              <h3>📡 Gestión WiFi</h3>
              <button
                onClick={loadWifiData}
                className="refresh-wifi-button"
                disabled={loadingWifi}
                title="Recargar información WiFi"
              >
                {loadingWifi ? '🔄' : '🔄'}
              </button>
            </div>

            {loadingWifi && !wifiData ? (
              <div className="wifi-loading">
                <p>Cargando información WiFi...</p>
              </div>
            ) : wifiData ? (
              <div className="wifi-info-content">
                {editingWifi ? (
                  <div className="wifi-edit-mode">
                    {/* SSID Editable */}
                    <div className="wifi-info-item">
                      <div className="wifi-label">🌐 Nombre de Red (SSID):</div>
                      <input
                        type="text"
                        value={newSSID}
                        onChange={(e) => setNewSSID(e.target.value)}
                        maxLength="32"
                        className="wifi-input"
                        placeholder="Nombre de la red WiFi"
                      />
                    </div>

                    {/* Contraseña Editable */}
                    <div className="wifi-info-item">
                      <div className="wifi-label">🔐 Contraseña WiFi:</div>
                      <div className="password-input-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          maxLength="63"
                          className="wifi-input"
                          placeholder="Mínimo 8 caracteres"
                        />
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="toggle-password-button"
                          type="button"
                        >
                          {showPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="wifi-action-buttons">
                      <button
                        onClick={handleUpdateWifi}
                        className="wifi-save-all-button"
                        disabled={loadingWifi}
                      >
                        ✅ Guardar Cambios
                      </button>
                      <button
                        onClick={() => {
                          setEditingWifi(false);
                          setNewSSID(wifiData.ssid);
                          setNewPassword(wifiData.password);
                          setShowPassword(false);
                        }}
                        className="wifi-cancel-button"
                        disabled={loadingWifi}
                      >
                        ❌ Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="wifi-display-mode">
                    {/* SSID Solo lectura */}
                    <div className="wifi-info-item">
                      <div className="wifi-label">🌐 Nombre de Red (SSID):</div>
                      <div className="wifi-value">{wifiData.ssid}</div>
                    </div>

                    {/* Contraseña Solo lectura */}
                    <div className="wifi-info-item">
                      <div className="wifi-label">🔐 Contraseña WiFi:</div>
                      <div className="wifi-display-controls">
                        <span className="wifi-value">
                          {showPassword ? wifiData.password : '••••••••'}
                        </span>
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="wifi-toggle-button"
                          title={showPassword ? "Ocultar" : "Mostrar"}
                        >
                          {showPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    {/* Dispositivos conectados */}
                    {wifiData.connectedCount !== undefined && (
                      <div className="wifi-info-item">
                        <div className="wifi-label">📱 Dispositivos Conectados:</div>
                        <span className="wifi-value wifi-devices-count">
                          {wifiData.connectedCount}
                        </span>
                      </div>
                    )}

                    {/* Botón Cambiar Todo */}
                    <div className="wifi-action-buttons">
                      <button
                        onClick={() => {
                          setEditingWifi(true);
                          setNewSSID(wifiData.ssid);
                          setNewPassword(wifiData.password);
                        }}
                        className="wifi-change-all-button"
                        title="Cambiar SSID y contraseña"
                      >
                        ✏️ Cambiar Todo
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="wifi-no-data">
                <p>ℹ️ No hay información WiFi disponible</p>
              </div>
            )}
          </div>
        </div>
      )}

      <RegisterPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        clientData={clientData}
        onPaymentRegistered={() => {
          // Recargar información del cliente después de registrar el pago
          loadClientData();
        }}
      />
    </div>
  );
};

export default ClientInfoPanel;

